import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from "moment-timezone";
import { approve_auction, auction_details } from '../../reduxData/user/userAction';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import { Box } from "@mui/joy";
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { useTranslation } from 'react-i18next';
import TranslateText from '../../components/TranslateText';
import { languageFields } from '../../hooks/LanguageFields';

const { REACT_APP_IMAGE_URL } = process.env;

const ApproveAuction = () => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useSelector((state) => state.auth);
    const [itemData, setItemData] = useState(null);
    const [reasonModal, setReasonModal] = useState(false);
    const [reason, setReason] = useState("");
    const [updatedVariants, setUpdatedVariants] = useState([]);
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [photoIndex, setPhotoIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentCaption, setCurrentCaption] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const normalizedStatus = typeof itemData?.status === "string"
        ? itemData.status.trim().toUpperCase()
        : itemData?.status;
    const isPendingProduct = ["PENDING", "PRODUCT_PENDING_APPROVAL"].includes(normalizedStatus);
    const normalizedDisplayStatus = ({
        PENDING: "PRODUCT_PENDING_APPROVAL",
        PRODUCT_LIVE: "PRODUCT_LIVE",
        PRODUCT_REJECTED: "PRODUCT_REJECTED",
        LIVE: "PRODUCT_LIVE",
        PRODUCT_PAUSED: "PRODUCT_ADMIN_PAUSED",
    })[normalizedStatus] || normalizedStatus;
    const statusTranslationKey = `itemListing.statuses.${normalizedDisplayStatus}`;
    const translatedStatus = t(statusTranslationKey);
    const statusLabel = translatedStatus === statusTranslationKey
        ? normalizedDisplayStatus?.replace(/_/g, " ").toLowerCase()
        : translatedStatus;
    const statusClass = normalizedDisplayStatus?.toLowerCase().replace(/_/g, "-") || "";
    const returnToListing = () => navigate('/item-listing', {
        state: { productTab: location.state?.productTab || "PRODUCT_PENDING_APPROVAL" },
    });

    const formatDate = (date) => {
        const newDateTime = moment(date).tz(userTimeZone);
        return newDateTime.format("YYYY-MM-DDTHH:mm");
    };

    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1, 20, 0, 0);
    const auction_start = formatDate(nextMonth);
    const endDate = new Date(nextMonth);
    endDate.setDate(4);
    endDate.setHours(20, 0, 0, 0);
    const auction_end = formatDate(endDate);

    // handle form submit function 
    const handleSubmit = async (e) => {
        setIsLoading(true);
        e.preventDefault();
        const data = {
            id: itemData?.id,
            auction_start: auction_start,
            auction_end: auction_end,
            status: 'PRODUCT_LIVE'
        };
        try {
            const resdata = await approve_auction(data, navigate, dispatch, "PRODUCT_LIVE");
            if (resdata?.status === true) {
                returnToListing();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // get product detail 
    useEffect(() => {
        if (!id) return;
        const handleFetch = async () => {
            const data = await auction_details(id, dispatch);
            setItemData(data);
        };
        handleFetch();
    }, [id, dispatch]);


    useEffect(() => {
        if (!itemData) return;
        const transformed = itemData?.variant_auction?.map(item => {
            const matchedVariants = itemData?.variants?.filter((v) => v?.color_name === item?.colors?.[0]?.color_name) || [];
            const allImages = matchedVariants[0]?.images || [];
            return {
                ...item,
                images: itemData?.origin === 'manual' ? allImages : item?.images || [],
            };
        });

        setUpdatedVariants(transformed);
    }, [itemData]);

    // get image url 
    const getFullImageUrl = (url) => {
        const checkExternalUrl = url ? (url?.startsWith("http") || url?.startsWith("https")) : false;
        const imageUrl = checkExternalUrl ? url : url ? `${REACT_APP_IMAGE_URL}${url}` : "";
        return imageUrl;
    };

    // handle reject product 
    const handleRejectReason = async () => {
        if (!reason.trim()) {
            alert(t("productDetail.reasonRequired"));
            return;
        }
        setIsLoading(true);
        const data = {
            id: itemData?.id,
            status: 'PRODUCT_REJECTED',
            reason: reason
        };
        try {
            const resdata = await approve_auction(data, navigate, dispatch, "PRODUCT_REJECTED");
            if (resdata?.status === true) {
                setReason("");
                setReasonModal(false);
                returnToListing();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // open image gallary 
    const openLightbox = (images, startIndex = 0, caption = "") => {
        setCurrentImages(images.map(getFullImageUrl));
        setCurrentCaption(caption);
        setPhotoIndex(startIndex);
        setIsOpen(true);
    };

    // add overflow hidden classes on body
    const addBodyClass = () => document.body.classList.add("overflow-hidden");

    // remove overflow hidden classes on body
    const removeBodyClass = () => document.body.classList.remove("overflow-hidden");

    return (
        <div className="product-detail-page">
            <div className="container">
                <div className="profile-deatils product-detail-card bg-white mt-4">
                    <div className="detail-heading px-3 py-3">
                        <div className="product-detail-heading-content">
                            <div className="d-flex align-items-center gap-2">
                                <button type="button" className="product-detail-back" onClick={returnToListing} aria-label={t("productDetail.backToProducts")}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                    </svg>
                                </button>
                                <h4 className="text-black fw-600">{t("productDetail.heading")}</h4>
                            </div>
                            {itemData && (
                                <span className={`product-detail-header-status ${statusClass}`}>
                                    {statusLabel}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="row">
                            <form className='auction-detail' onSubmit={handleSubmit} noValidate>
                                <div className="product-detail-summary">
                                    <div className="mb-3">
                                        <div className="row">
                                            <div className="col-md-6 col-lg-6">
                                                <label className="mb-2">{t("productDetail.sellerName")}</label>
                                                <div className="password position-relative">
                                                    <input type="text" className="form-control" disabled value={itemData?.seller?.user_name ?? ''} />
                                                </div>
                                            </div>
                                            <div className="col-md-6 col-lg-6">
                                                <label className="mb-2">{t("productDetail.sellerEmail")}</label>
                                                <div className="password position-relative">
                                                    <input type="text" className="form-control" disabled value={itemData?.seller?.seller_email ?? ''} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row">
                                            <div className="col-md-6 col-lg-6">
                                                <label className="mb-2">{t("productDetail.brandName")}</label>
                                                <input type="text" className="form-control" disabled value={itemData?.brand_name ?? ''} />
                                            </div>
                                            <div className="col-md-6 col-lg-6">
                                                <label className="mb-2">{t("productDetail.title")}</label>
                                                <input type="text" className="form-control" disabled value={languageFields(itemData, "title", i18n)} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row">
                                            {itemData?.article_number &&
                                                <div className="col-md-6 col-lg-6">
                                                    <label className="mb-2">{t("productDetail.articleNumber")}</label>
                                                    <input type="text" className="form-control" disabled value={itemData?.article_number ?? '-'} />
                                                </div>
                                            }
                                            <div className="col-md-6 col-lg-6">
                                                <label className="mb-2">{t("productDetail.condition")}</label>
                                                <input type="text" className="form-control" disabled value={languageFields(itemData, "condition", i18n) || '-'} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <label className="mb-2">{t("productDetail.category")}</label>
                                                <input type="text" className="form-control" disabled value={languageFields(itemData?.subcategory_data, "name", i18n) || '-'} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row">
                                            <div className="col-md-3 col-lg-3">
                                                <label className="mb-2">{t("productDetail.shopPrice")}</label>
                                                <input type="number" className="form-control" disabled value={itemData?.original_retail_price ? Number(itemData?.original_retail_price) : 0} />
                                            </div>
                                            {itemData?.include_in_final_sale && (
                                                <div className="col-md-3 col-lg-3">
                                                    <label className="mb-2">{t("productDetail.finalSaleStartPrice")}</label>
                                                    <input type="number" className="form-control" disabled value={itemData?.minimum_start_price ? Number(itemData?.minimum_start_price) : 0} />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="product-description-block mb-3">
                                        <label className="mb-2">{t("productDetail.description")}</label>
                                        <div className="product-readonly-value product-readonly-value--multiline">
                                            {languageFields(itemData, "discription", i18n) || '-'}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="mb-2">{t("productDetail.materialEstimate")}</label>
                                                <div className="product-readonly-value">
                                                    {languageFields(itemData, "material_estimate", i18n) || '-'}
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="mb-2">{t("productDetail.fit")}</label>
                                                <div className="product-readonly-value">
                                                    {languageFields(itemData, "fit", i18n) || '-'}
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="mb-2">{t("productDetail.careInstructions")}</label>
                                                <div className="product-readonly-value">
                                                    {languageFields(itemData, "care_instructions", i18n, "care_instruction_nl") || '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {updatedVariants?.length > 0 && (
                                        <div className="product-variants-section mb-3">
                                            <div className="product-section-heading">
                                                <span>{t("productDetail.variants")}</span>
                                                <small>{updatedVariants.length}</small>
                                            </div>
                                            {updatedVariants?.map((item, index) => (
                                                <section className="product-variant-card" key={index}>
                                                    <div className="row">
                                                        <div className='variant-box'>{t("productDetail.variant")} {index + 1}</div>

                                                        <div className="col-md-12 col-lg-12 mb-3">
                                                            <label className="mb-2">{t("productDetail.featuredImage")}</label>
                                                            <div className="d-flex gap-3 align-items-center">
                                                                {item?.colors?.map((itm, i) => (
                                                                    <div key={i}>
                                                                        <div className="position-relative pb-3 d-flex media-data-img-wrap">
                                                                            <img
                                                                                src={getFullImageUrl(item?.images[0])}
                                                                                className="item-img"
                                                                                alt={itm?.color_name}
                                                                            />
                                                                        </div>
                                                                        <TranslateText
                                                                            text={languageFields(itm, "color_name", i18n)}
                                                                            fallback="-"
                                                                            className="color-name d-block text-center"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div className="col-md-12 col-lg-12 mb-3">
                                                            <label className="mb-2">{t("productDetail.sizes")}</label>
                                                            <div className="row g-3">
                                                                {item?.sizes?.map((sizeObj, i) => (
                                                                    <div className="col-6 col-md-4 col-lg-2" key={i}>
                                                                        <div className="card text-center shadow-sm border-0 h-100 size-card" style={{ borderRadius: "10px", backgroundColor: "#f8f9fa" }}>
                                                                            <div className="card-body py-3">
                                                                                <h5 className="card-title mb-2 fw-semibold" style={{ color: "#333" }}>
                                                                                    {sizeObj?.size_name}
                                                                                </h5>
                                                                                <span className={`badge ${sizeObj?.quantity > 5 ? "bg-success" : sizeObj?.quantity > 0 ? "bg-warning text-dark" : "bg-danger"}`}>
                                                                                    {sizeObj?.quantity} {t("productDetail.inStock")}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {item?.specific_color && (
                                                            <div className="col-md-12 mt-3">
                                                                <label className="mb-2">{t("productDetail.specificColor")}</label>
                                                                <div className="inputfield"><TranslateText text={item?.specific_color} fallback="-" /></div>
                                                            </div>
                                                        )}

                                                        <div className='row mt-3'>
                                                            {item?.pattern && (
                                                                <div className="col-md-6">
                                                                    <label className="mb-2">{t("productDetail.pattern")}</label>
                                                                    <div className="inputfield"><TranslateText text={item?.pattern} fallback="-" /></div>
                                                                </div>
                                                            )}
                                                            {item?.finish && (
                                                                <div className="col-md-6">
                                                                    <label className="mb-2">{t("productDetail.finish")}</label>
                                                                    <div className="inputfield"><TranslateText text={item?.finish} fallback="-" /></div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="col-md-12 col-lg-12">
                                                            <label className="mb-2 mt-3">{t("productDetail.variantImages")}</label>
                                                            <div className="d-flex flex-row flex-wrap gap-3">
                                                                {item?.images?.map((img, imgIndex) => (
                                                                    <div
                                                                        key={imgIndex}
                                                                        className="position-relative pb-3 d-flex media-data-img-wrap cursor-pointer"
                                                                        onClick={() => { openLightbox(item.images, imgIndex, `${t("productDetail.variant")} ${index + 1} - ${item?.colors?.[0]?.color_name || t("productDetail.image")}`); addBodyClass() }}
                                                                        style={{ cursor: 'zoom-in' }}
                                                                    >
                                                                        <img
                                                                            src={getFullImageUrl(img)}
                                                                            alt={`${t("productDetail.variant")} ${index + 1} - ${t("productDetail.image")} ${imgIndex + 1}`}
                                                                            className="item-img"
                                                                        />
                                                                        <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center pb-2">
                                                                            <small className="bg-dark bg-opacity-75 text-white px-2 py-1 rounded">{t("productDetail.clickToZoom")}</small>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {itemData?.include_in_final_sale && (
                                    <div className="product-auction-note mb-4">
                                        <span>{t("productDetail.auctionSchedule")}</span>
                                    </div>
                                )}

                                {userPermissions?.products?.view && userPermissions?.products?.edit && isPendingProduct && (
                                    <div className="col-md-12 col-lg-12">
                                        <div className="product-detail-actions d-flex gap-3 pt-4">
                                            <button className="btn common-button" type="submit" disabled={isLoading}>
                                                {t("productDetail.approve")} {isLoading && '...'}
                                            </button> 
                                            <button
                                                type='button'
                                                className="reject-btn"
                                                onClick={() => setReasonModal(true)}
                                            >
                                                {t("productDetail.reject")}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <Modal open={reasonModal} onClose={() => setReasonModal(false)}>
                <ModalDialog>
                    <DialogTitle>{t("productDetail.rejectAuction")}</DialogTitle>
                    <DialogContent>
                        <div className="reject-reason-container">
                            <label htmlFor="reason" className="reject-reason-label">
                                {t("productDetail.reasonForRejection")} *
                            </label>
                            <textarea
                                name="reason"
                                id="reason"
                                className="reject-reason-textarea"
                                placeholder={t("productDetail.reasonPlaceholder")}
                                rows="5"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className='d-flex gap-2 justify-content-end'>
                            <button className='model-no-btn' onClick={() => setReasonModal(false)}>
                                {t("productDetail.cancel")}
                            </button>
                            <button
                                className={`model-yes-btn ${!reason.trim() && 'modal-yes-btn-disable'}`}
                                onClick={handleRejectReason}
                                disabled={!reason.trim() || isLoading}
                            >
                                {t("productDetail.reject")} {isLoading && '...'}
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>

            {isOpen && (
                <Lightbox
                    mainSrc={currentImages[photoIndex]}
                    nextSrc={currentImages[(photoIndex + 1) % currentImages.length]}
                    prevSrc={currentImages[(photoIndex + currentImages.length - 1) % currentImages.length]}
                    onCloseRequest={() => {
                        setIsOpen(false);
                        removeBodyClass()
                    }}
                    onMovePrevRequest={() =>
                        setPhotoIndex((photoIndex + currentImages.length - 1) % currentImages.length)
                    }
                    onMoveNextRequest={() =>
                        setPhotoIndex((photoIndex + 1) % currentImages.length)
                    }
                    imageTitle={`${t("productDetail.image")} ${photoIndex + 1} ${t("productDetail.of")} ${currentImages.length}`}
                    imageCaption={currentCaption}
                    enableZoom={true}
                    clickOutsideToClose={true}
                    discourageDownloads={true}
                    animationDuration={300}
                    keyRepeatKeyup={true}
                />
            )}
        </div>
    );
};

export default ApproveAuction;
